/**
 * Contractor Performance Selectors (P9)
 * ──────────────────────────────────────
 * Pure derivations for the Profile → Performance section. All metrics are:
 *   • read-only
 *   • derived from existing canonical data (jobs + executions + offers)
 *   • sample-size guarded — return `null` when n < MIN_SAMPLE_SIZE
 *   • never expose customer revenue or job-level financial state
 *   • no gamification, no rankings
 *
 * Reuses canonical resolvers:
 *   • acceptanceRate          → getContractorOfferMetrics() (P2)
 *   • realized earnings trend → resolveJobPayoutSnapshot() + getJobCompletionDate()
 */

import type { Job } from "@/data/types";
import type { JobSpecExecution, PhaseExecution } from "@/data/jobSpecExecution";
import type { MarketplaceOffer } from "./marketplaceOffer";
import {
  filterContractorJobs,
  getJobCompletionDate,
  resolveJobPayoutSnapshot,
} from "./commandCenter";

export const PERF_MIN_SAMPLE = 3;

export interface ContractorPerformanceMetrics {
  /** Median minutes between scheduledInstallDate and first phase startedAt. null when n<3. */
  onTimeCheckInRate: number | null;
  /** Median ms between phase start → submission across phases. null when n<3. */
  medianPhaseSubmissionMs: number | null;
  /** qa_passed / (qa_passed + qa_failed). null when n<3. */
  qaPassRate: number | null;
  /** 4 weekly buckets (oldest→newest), counts of completed jobs. Sparkline-only. */
  earningsTrendBuckets: number[];
  /** Sample sizes for transparency. */
  samples: {
    checkInsObserved: number;
    phasesSubmitted: number;
    qaReviewed: number;
    completedIn30Days: number;
  };
}

const DAY_MS = 24 * 60 * 60 * 1000;

function median(nums: number[]): number {
  const sorted = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

/**
 * Compute performance metrics for a contractor.
 *
 * On-time check-in rule (locked):
 *   "On-time" = first phase startedAt is on or before scheduledInstallDate
 *   end-of-day (23:59 local). Returns the % of observable jobs that were
 *   on-time. Requires ≥3 observable check-ins.
 *
 * Phase submission timing (locked):
 *   For every phase that has both startedAt and submittedAt, compute
 *   (submittedAt - startedAt). Median across all phases. Requires ≥3 phases.
 *
 * QA pass rate (locked):
 *   passed / (passed + failed). Requires ≥3 reviewed phases.
 *
 * Earnings trend (locked):
 *   4 weekly buckets ending today. Each bucket = COUNT of completed jobs in
 *   that week (NOT dollars — sparkline-only, no $ axis). Always returned;
 *   `null` semantics don't apply because count = 0 is meaningful.
 */
export function getContractorPerformanceMetrics(args: {
  contractorId: string;
  matchKey: string;
  jobs: Job[];
  executions: JobSpecExecution[];
  offers: MarketplaceOffer[];
  now?: Date;
}): ContractorPerformanceMetrics {
  const { contractorId, matchKey, jobs, executions, now = new Date() } = args;
  const myJobs = filterContractorJobs(jobs, contractorId, matchKey);
  const myJobIds = new Set(myJobs.map((j) => j.id));
  const myExecs = executions.filter((e) => myJobIds.has(e.jobId));

  /* ── On-time check-in ─────────────────────────────────────────── */
  const checkInOutcomes: number[] = []; // 1 = on-time, 0 = late
  for (const job of myJobs) {
    if (!job.scheduledInstallDate) continue;
    const exec = myExecs.find((e) => e.jobId === job.id);
    const firstPhase = exec?.phases[0];
    const startedAt = (firstPhase as PhaseExecution & { startedAt?: string } | undefined)
      ?.startedAt;
    if (!startedAt) continue;
    const sched = new Date(job.scheduledInstallDate + "T23:59:59").getTime();
    checkInOutcomes.push(new Date(startedAt).getTime() <= sched ? 1 : 0);
  }
  const onTimeCheckInRate =
    checkInOutcomes.length >= PERF_MIN_SAMPLE
      ? checkInOutcomes.reduce((a, b) => a + b, 0) / checkInOutcomes.length
      : null;

  /* ── Phase submission timing ──────────────────────────────────── */
  const submissionDeltas: number[] = [];
  for (const exec of myExecs) {
    for (const phase of exec.phases) {
      const ext = phase as PhaseExecution & {
        startedAt?: string;
        submittedAt?: string;
      };
      if (!ext.startedAt || !ext.submittedAt) continue;
      const delta =
        new Date(ext.submittedAt).getTime() - new Date(ext.startedAt).getTime();
      if (delta > 0) submissionDeltas.push(delta);
    }
  }
  const medianPhaseSubmissionMs =
    submissionDeltas.length >= PERF_MIN_SAMPLE ? median(submissionDeltas) : null;

  /* ── QA pass rate ─────────────────────────────────────────────── */
  let passed = 0;
  let failed = 0;
  for (const exec of myExecs) {
    for (const phase of exec.phases) {
      if (phase.status === "qa_passed") passed++;
      else if (phase.status === "qa_failed") failed++;
    }
  }
  const qaTotal = passed + failed;
  const qaPassRate = qaTotal >= PERF_MIN_SAMPLE ? passed / qaTotal : null;

  /* ── Earnings trend (count per week, last 4 weeks) ────────────── */
  const buckets = [0, 0, 0, 0]; // oldest → newest
  let completedIn30Days = 0;
  for (const job of myJobs) {
    const completion = getJobCompletionDate(job);
    if (!completion) continue;
    const ageDays = Math.floor(
      (now.getTime() - new Date(completion + "T00:00:00").getTime()) / DAY_MS,
    );
    if (ageDays < 0 || ageDays >= 28) continue;
    completedIn30Days++;
    const bucket = 3 - Math.floor(ageDays / 7); // 0..3
    if (bucket >= 0 && bucket <= 3) buckets[bucket]++;
  }

  return {
    onTimeCheckInRate,
    medianPhaseSubmissionMs,
    qaPassRate,
    earningsTrendBuckets: buckets,
    samples: {
      checkInsObserved: checkInOutcomes.length,
      phasesSubmitted: submissionDeltas.length,
      qaReviewed: qaTotal,
      completedIn30Days,
    },
  };
}
